/**
 * `useVisitorSession` — the React binding over `visitorStore` that drives the
 * widget's session lifecycle (ARCHITECTURE §7):
 *
 *   loadWidgetConfig → applyWidgetTheme → (resume | prechat) → createSession
 *   → live over WS → send messages (optimistic) → close/leave.
 *
 * All calls run against the live public widget API (`api.widget.*`): create is
 * anonymous and returns the resume token; resume/send carry the opaque session
 * token (attached by the shared client's `getToken`, which reads `visitorStore`).
 */

import { useCallback, useSyncExternalStore } from 'react';
import type {
  Message,
  PreChatFormValues,
  WidgetConfig,
} from '@flozic/shared/types';
import { ApiError } from '@flozic/shared/api';
import type { EmbedContext } from '../config/parseEmbed';
import { getWidgetApi } from '../config/loadWidgetConfig';
import { visitorStore, type VisitorState } from './visitorStore';
import { getVisitorId } from '../presence/useVisitorPresence';

/** Generate a client-side temp id for optimistic-send reconciliation. */
function newClientMsgId(): string {
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Capture visitor context to send on session create. The reliable HOST page URL is the one the
 * loader forwarded (`embed.pageUrl`); `document.referrer` inside the cross-origin iframe is only
 * the host ORIGIN under the default Referrer-Policy, so it's a lossy fallback. Locale/screen come
 * from the visitor's browser; the server fills IP + UA-derived browser/OS/device. All access is
 * guarded so an odd embedding environment never throws.
 */
function captureVisitorContext(hostUrl?: string | null): Record<string, unknown> {
  const ctx: Record<string, unknown> = {};
  try {
    // The anonymous visitor id links this session to the presence journey ("View visitor path").
    ctx.visitorId = getVisitorId();
    const pageUrl = hostUrl || (typeof document !== 'undefined' ? document.referrer : '');
    if (pageUrl) ctx.pageUrl = pageUrl;
    if (typeof navigator !== 'undefined' && navigator.language) ctx.language = navigator.language;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) ctx.timezone = tz;
    if (typeof window !== 'undefined' && window.screen) {
      ctx.screen = `${window.screen.width}x${window.screen.height}`;
    }
  } catch {
    /* unsupported environment — send whatever we managed to collect */
  }
  return ctx;
}

/** Public API returned by the hook. */
export interface UseVisitorSession {
  state: VisitorState;
  /** Open/close the launcher panel. */
  setOpen: (open: boolean) => void;
  /** Start a session from pre-chat form values. */
  startSession: (values: PreChatFormValues) => Promise<void>;
  /**
   * Resume a persisted session (if a valid token + session id exist).
   * Resolves `true` when resume landed on a definite phase itself — either a live
   * session (phase → `chatting`) or a recently-closed/left one (phase → `ended`,
   * so the visitor can still see/submit CSAT feedback) — meaning the caller must
   * NOT fall through to its own prechat/offline decision. Resolves `false` when
   * there was nothing to resume (no token, or an invalid/expired token, which IS
   * cleared in that case).
   */
  resumeSession: () => Promise<boolean>;
  /** Send a visitor message (optimistic append + server reconcile). */
  sendMessage: (body: string) => Promise<void>;
  /** Upload + send a file attachment (image/PDF). */
  sendAttachment: (file: File) => Promise<void>;
}

/**
 * Bind the widget session store into React and expose lifecycle actions scoped
 * to the given embed + widget config.
 */
export function useVisitorSession(
  embed: EmbedContext,
  config: WidgetConfig | null,
): UseVisitorSession {
  const state = useSyncExternalStore(visitorStore.subscribe, visitorStore.getSnapshot);

  const setOpen = useCallback((open: boolean) => visitorStore.setOpen(open), []);

  const startSession = useCallback(
    async (values: PreChatFormValues) => {
      try {
        visitorStore.setError(null);
        const api = getWidgetApi();
        const { session, sessionToken } = await api.widget.createSession(embed.cid, {
          preChatForm: values,
          context: embed.context ?? undefined,
          visitorContext: captureVisitorContext(embed.pageUrl),
        });
        // Persist the resume token + session id, then set them in-memory. Setting
        // the token opens the WS (WidgetApp passes `state.sessionToken` down).
        visitorStore.setSessionToken(embed.cid, sessionToken);
        visitorStore.persistSessionId(embed.cid, session.id);
        visitorStore.setSession(session);
        visitorStore.setPhase('chatting');
      } catch (err) {
        // Daily conversation cap (429/daily_limit_reached): the org's plan is exhausted for
        // today — show the visitor the normal offline leave-a-message experience, never a
        // dead-end error (and never anything about the org's billing).
        if (
          err instanceof ApiError &&
          (err.code === 'daily_limit_reached' || err.status === 429)
        ) {
          visitorStore.setPhase('offline');
          return;
        }
        visitorStore.setError(err instanceof Error ? err.message : 'Failed to start chat');
      }
    },
    [embed],
  );

  const resumeSession = useCallback(async (): Promise<boolean> => {
    const token = visitorStore.readPersistedToken(embed.cid);
    const sessionId = visitorStore.readPersistedSessionId(embed.cid);
    if (!token || !sessionId) return false;

    // Set the token in-memory first so the client attaches it to `getSession`.
    visitorStore.setSessionToken(embed.cid, token);
    try {
      const api = getWidgetApi();
      const session = await api.widget.getSession(embed.cid, sessionId);
      visitorStore.setSession(session);

      // A closed/left session should not resume into the LIVE chat view — but the same widget
      // session token is the only credential the CSAT rating endpoint still accepts, so wiping
      // it here (as this used to do) permanently locked the visitor out of submitting OR editing
      // feedback the moment the tab reloaded. Instead, keep the persisted token + session id and
      // land on the 'ended' screen so the rating form is reachable again. This only ever
      // resurfaces the MOST RECENT session — exactly one token/session id is persisted per embed
      // id, and starting a new chat (`EndedScreen`'s restart handler) explicitly clears it — and
      // is further bounded by the token's own TTL (an expired/invalid token still 401s/404s below
      // and IS cleared in the catch branch).
      if (session.status === 'closed' || session.status === 'left') {
        visitorStore.setPhase('ended');
        return true;
      }

      // Rehydrate the transcript so a page reload doesn't drop the conversation history
      // (previously the widget resumed into an empty message list). Best-effort: a failed
      // history fetch still resumes the live chat (new messages arrive over the socket).
      try {
        const history = await api.widget.history(embed.cid, sessionId);
        visitorStore.setMessages(history);
      } catch {
        /* history unavailable — resume anyway; the socket carries new messages */
      }

      visitorStore.setPhase('chatting');
      return true;
    } catch (err) {
      // Token invalid/expired → clear it and fall back to a fresh pre-chat.
      // Transient network errors are left alone so a later reload can retry.
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
        visitorStore.setSessionToken(embed.cid, null);
      }
      return false;
    }
  }, [embed]);

  const sendMessage = useCallback(
    async (body: string) => {
      const text = body.trim();
      if (!text) return;
      const session = visitorStore.getSnapshot().session;
      if (!session) return;
      const clientMsgId = newClientMsgId();

      // Optimistic append (queued) so the composer feels instant.
      const optimistic: Message = {
        id: clientMsgId,
        sessionId: session.id,
        channel: 'inbuilt',
        senderType: 'visitor',
        direction: 'inbound',
        senderId: session.visitorId ?? null,
        body: text,
        media: [],
        deliveryStatus: 'queued',
        providerMessageId: null,
        clientMsgId,
        createdAt: new Date().toISOString(),
      };
      visitorStore.upsertMessage(optimistic);

      try {
        const api = getWidgetApi();
        // Reconcile the server row by `clientMsgId` (the WS echo dedupes too).
        const saved = await api.widget.send(embed.cid, session.id, {
          body: text,
          clientMsgId,
        });
        visitorStore.upsertMessage(saved);
      } catch (err) {
        visitorStore.upsertMessage({ ...optimistic, deliveryStatus: 'failed' });
        void err;
      }
    },
    [embed],
  );

  const sendAttachment = useCallback(
    async (file: File) => {
      const session = visitorStore.getSnapshot().session;
      if (!session) return;
      const clientMsgId = newClientMsgId();
      try {
        const api = getWidgetApi();
        const media = await api.widget.uploadMedia(embed.cid, session.id, file);
        const optimistic: Message = {
          id: clientMsgId,
          sessionId: session.id,
          channel: 'inbuilt',
          senderType: 'visitor',
          direction: 'inbound',
          senderId: session.visitorId ?? null,
          body: null,
          media: [media],
          deliveryStatus: 'queued',
          providerMessageId: null,
          clientMsgId,
          createdAt: new Date().toISOString(),
        };
        visitorStore.upsertMessage(optimistic);
        const saved = await api.widget.send(embed.cid, session.id, { media: [media], clientMsgId });
        visitorStore.upsertMessage(saved);
      } catch (err) {
        void err; // upload/send failed — the file simply isn't attached; visitor can retry
      }
    },
    [embed],
  );

  // `config` participates in the hook's contract; screens read it directly, so
  // it is not consumed here.
  void config;

  return { state, setOpen, startSession, resumeSession, sendMessage, sendAttachment };
}
